import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { object, string, array, bool } from 'yup';
import { Formik, Form, getIn } from 'formik';
import get from 'lodash/get';
import set from 'lodash/set';
import remove from 'lodash/remove';

import { useTranslation } from 'react-i18next';
import { debounce, uniqBy } from 'lodash';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { TP, PERFORMANCE_TYPES_ID, urlRegex, VISIBILITY } from '../../../../constants/consts';
import { getOption, createSelectFieldOptions } from '../../../../utils/common';
import EditModeCard, { EditModeCardSection } from '../../../../components/EditModeCard';
import MusicalWorkComposerEditFields from './MusicalWorkComposerEditFields';
import CoProducedCompaniesEditFields from './CoProducedCompaniesEditFields';
import LangsEditFields from './LangsEditFields';
import FestivalsEditFields from './FestivalsEditFields';
import BookingInfoEditFields from './BookingInfoEditFields';
import TypesEditFields from './TypesEditFields';
import { IS_CO_PRODUCED, musicalWorkFields } from '../../../../constants/formFields';
import FormikOnError from '../../../../components/Fields/FormikOnError';
import { cleanAggregationsAction, getLanguagesAction, getList, getWholeList } from '../../../App/actions';
import {
  FESTIVALS,
  MUSICAL_WORK_ENDPOINT,
  LANGUAGES_LIST,
  COMPOSER,
  COMPANIES,
} from '../../../../constants/endpointConstants';
import {
  cleanOptionsValuesAction,
  getCompaniesAction,
  getComposersAction,
  getFestivalsAction,
  getWorksAction,
} from '../../actions';
import { selectAggregationsLoading, selectLanguagesOptions } from '../../../App/selectors';
import {
  selectCompaniesOptions,
  selectComposersOptions,
  selectFestivalsOptions,
  selectLoadingFieldName,
  selectWorksOptionsData,
} from '../../selectors';
import MainCompanyFields from './MainCompanyFields';
import PerformanceTypeInfoBlock from '../PerformanceTypeInfoBlock';
import DisclaimerBox from '../../../../components/EditComponents/DisclaimerBox';
import { EDITING_PRODUCTION_ENTITY_TYPES } from '../../constants';

const useStyles = makeStyles(theme => ({
  addFields: {
    width: '60%',
    maxWidth: 1000,
    [theme.breakpoints.down('sm')]: {
      width: '80%',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  section: {
    marginTop: '2rem',
  },
  companyCheckboxSection: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  companyCheckbox: {
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },
  },
}));

const {
  composerField,
  workField,
  name,
  doubleBillField,
  productionTypesField,
  companyField,
  coProducedCompaniesField,
  festivalsField,
  languageField,
  subtitlesField,
  productionPageField,
  boxOfficePageField,
  bookingDateField,
  bookingTimeField,
  doubleWorks,
  isCompanyUnknownField,
} = musicalWorkFields;

const ProductionUpdateMusicalWorkForm = ({
  forwardRef,
  production,
  submitFunc,
  composers,
  loadingAggValue,
  works,
  companiesList,
  festivals,
  langs,
  addOption,
  loadingCompanyFieldName,
  company,
  type,
  // artist additional functions
  onWorkChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (langs?.length === 0) {
      dispatch(getWholeList({ endpoint: LANGUAGES_LIST, sagaRoutine: getLanguagesAction }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchWorkFilters = debounce((fieldName, query, value) => {
    dispatch(
      getList({
        endpoint: MUSICAL_WORK_ENDPOINT,
        sagaRoutine: getWorksAction,
        fieldName,
        params: {
          queryParams: {
            query,
            [musicalWorkFields?.composerField?.name]: value,
            show_invalid: true,
            visibility: VISIBILITY.ALL,
          },
        },
      }),
    );
  }, 300);

  const searchComposersFilters = debounce((fieldName, query) => {
    dispatch(
      getList({
        endpoint: COMPOSER,
        sagaRoutine: getComposersAction,
        fieldName,
        params: {
          queryParams: {
            query,
            show_invalid: true,
            visibility: VISIBILITY.ALL,
          },
        },
      }),
    );
  }, 300);

  const getFestivals = debounce((fieldName, query) => {
    dispatch(
      getList({
        endpoint: FESTIVALS,
        sagaRoutine: getFestivalsAction,
        fieldName,
        params: {
          queryParams: {
            query,
            show_invalid: true,
            visibility: VISIBILITY.ALL,
          },
        },
      }),
    );
  }, 300);

  const getCompaniesList = debounce((fieldName, query) => {
    dispatch(
      getList({
        endpoint: COMPANIES,
        sagaRoutine: getCompaniesAction,
        fieldName,
        params: {
          queryParams: {
            query,
            show_invalid: true,
            visibility: VISIBILITY.ALL,
          },
        },
      }),
    );
  }, 300);

  const resetMusicWorksOptions = () => dispatch(cleanOptionsValuesAction('works'));

  const resetOptions = field => {
    dispatch(cleanAggregationsAction(field));
  };

  const getLanguagesOptions = fieldName => [
    ...createSelectFieldOptions(
      get(production, fieldName).filter(el => !el.languageReference),
      'id',
      'name',
    ),
    ...createSelectFieldOptions(
      get(production, fieldName).filter(el => el.languageReference),
      'languageReference',
      'languageReference',
      { __isNew__: true },
    ),
  ];

  const setFormValues = productionToSet => {
    remove(productionToSet?.companies, { id: productionToSet?.company?.id });
    const types = {};
    const festivalCompanies = [...(company?.companies || [])];
    const allTypes = [...get(productionToSet, productionTypesField.productionName, [])];
    const doubleBillType = remove(allTypes, { id: PERFORMANCE_TYPES_ID.DOUBLE_BILL })[0];
    let coProducedCompanies = [...(productionToSet?.companies || [])];
    if (!productionToSet?.id) {
      coProducedCompanies = type === EDITING_PRODUCTION_ENTITY_TYPES.FESTIVAL ? festivalCompanies?.splice(1) : [];
    }
    const doubleWorksList = !production?.works || production?.works?.length === 0 ? [''] : production?.works;
    const houseCompany = type === EDITING_PRODUCTION_ENTITY_TYPES.FESTIVAL ? festivalCompanies[0] : company;
    allTypes.forEach(prodType => {
      set(types, prodType?.id, true);
    });

    if (productionToSet?.festivals?.length > 0) {
      set(types, PERFORMANCE_TYPES_ID.FESTIVAL, true);
    }
    if (coProducedCompanies.length > 0) {
      set(types, IS_CO_PRODUCED, true);
    }
    const prodFestivals = productionToSet?.festivals || [];
    if (type === EDITING_PRODUCTION_ENTITY_TYPES.FESTIVAL && !production?.id) {
      set(types, PERFORMANCE_TYPES_ID.FESTIVAL, true);
      prodFestivals.push(company);
    }

    return {
      [composerField.name]:
        addOption?.composer ||
        (productionToSet?.work?.composer?.id ? getOption(productionToSet?.work?.composer) : composerField.value),
      [workField.name]:
        addOption?.work ||
        (productionToSet?.work?.id
          ? {
              value: productionToSet?.work?.id,
              label: productionToSet?.work?.original_name || productionToSet?.work?.name,
            }
          : workField.value),
      [name.name]: productionToSet?.name || name?.value,
      [doubleBillField.name]: Boolean(doubleBillType) || doubleBillField.value,
      [doubleWorks.name]: doubleWorksList.map(work => ({
        work: work ? getOption(work) : '',
        composer: work?.composer ? getOption(work?.composer) : '',
      })),
      [productionTypesField.name]: types,
      [companyField.name]: getOption(production?.company || houseCompany),
      [isCompanyUnknownField.name]: productionToSet?.id ? !(productionToSet.company || houseCompany) : false,
      [coProducedCompaniesField.name]:
        coProducedCompanies?.length > 0
          ? createSelectFieldOptions(coProducedCompanies, 'id', 'name')
          : coProducedCompaniesField.value,
      [festivalsField.name]:
        prodFestivals?.length > 0 ? createSelectFieldOptions(prodFestivals, 'id', 'name') : festivalsField.value,
      [languageField.name]:
        get(productionToSet, languageField.productionName, []).length > 0
          ? getLanguagesOptions(languageField.productionName)
          : languageField.value,
      [subtitlesField.name]:
        get(productionToSet, subtitlesField.productionName, []).length > 0
          ? getLanguagesOptions(subtitlesField.productionName)
          : subtitlesField.value,
      [productionPageField.name]: productionToSet[productionPageField.productionName] || '',
      [boxOfficePageField.name]: productionToSet[boxOfficePageField.productionName] || '',
      [bookingDateField.name]: get(productionToSet, bookingDateField.productionName, ''),
      [bookingTimeField.name]: (get(productionToSet, bookingTimeField.productionName, '') || '').slice(0, 5), // hh:mm from api hh:mm:ss
    };
  };

  const [initialValues, setInitialValues] = React.useState(setFormValues(production));

  React.useEffect(() => {
    setInitialValues(setFormValues(production));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [production]);

  const getIdsFromOptions = (options, flag = {}) => {
    const optionsIds = [];
    options.forEach(option => {
      // eslint-disable-next-line no-underscore-dangle
      if (option?.value) optionsIds.push({ [option.__isNew__ ? 'name' : 'id']: option?.value, ...flag });
    });
    return optionsIds;
  };

  const getProductionTypeValue = (values, typeKey) => getIn(values, `${productionTypesField.name}.${typeKey}`, false);

  const submit = values => {
    const appliedTypes = [];
    const allTypes = { ...values[productionTypesField.name] };
    delete allTypes[IS_CO_PRODUCED];
    Object.keys(allTypes).forEach(typeKey => {
      if (get(values, `${productionTypesField.name}.${typeKey}`)) appliedTypes.push({ id: Number(typeKey) });
    });
    const worksList = [];
    if (values[doubleBillField.name]) {
      appliedTypes.push({ id: doubleBillField.name });
      get(values, doubleWorks.name, []).forEach(mwFieldGroup => {
        const mwId = get(mwFieldGroup, 'work.value');
        if (mwId) {
          worksList.push({ id: mwId, composer: { id: mwFieldGroup?.composer?.value } });
        }
      });
    }
    const coProdusedCompanies = getProductionTypeValue(values, IS_CO_PRODUCED)
      ? getIdsFromOptions(values[coProducedCompaniesField.name])
      : [];
    const organiserCompanyId = get(values, isCompanyUnknownField.name) ? null : values[companyField.name]?.value;
    if (organiserCompanyId) {
      coProdusedCompanies.push({ id: organiserCompanyId });
    }

    const paramsToSet = {
      [companyField.name]: organiserCompanyId ? { id: organiserCompanyId } : null,
      [doubleWorks.productionName]: worksList,
      [coProducedCompaniesField.productionName]: coProdusedCompanies,
      [festivalsField.productionName]: getProductionTypeValue(values, PERFORMANCE_TYPES_ID.FESTIVAL)
        ? uniqBy(getIdsFromOptions(values[festivalsField.name]), 'id')
        : [],
      [languageField.productionName]: [
        ...getIdsFromOptions(values[languageField.name]),
        ...getIdsFromOptions(values[subtitlesField.name], { is_subtitle: true }),
      ],
      [productionTypesField.productionName]: appliedTypes,
      [name.productionName]: values[name.name],
      [workField.productionName]: { id: values[workField.name]?.value },
    };

    [productionPageField, boxOfficePageField, bookingDateField, bookingTimeField].forEach(field => {
      set(paramsToSet, field.productionName, values[field.name]);
    });
    submitFunc(paramsToSet);
  };

  return (
    <EditModeCard>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          submit(values);
          setSubmitting(false);
        }}
        innerRef={forwardRef}
        validationSchema={() => productionUpdateMusicalWorkSchema(t)}
      >
        {({ values, setFieldValue, setValues }) => (
          <Form>
            <FormikOnError>
              <EditModeCardSection first>
                <MusicalWorkComposerEditFields
                  composerField={composerField}
                  workField={workField}
                  nameField={name}
                  doubleBillField={doubleBillField}
                  doubleWorks={doubleWorks}
                  composers={composers}
                  works={works?.options}
                  searchWorkFilters={searchWorkFilters}
                  searchComposersFilters={searchComposersFilters}
                  loadingValue={loadingCompanyFieldName || loadingAggValue}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                  values={values}
                  resetMusicWorksOptions={resetMusicWorksOptions}
                  disabled={Boolean(production?.id)}
                  onWorkChange={onWorkChange}
                  resetOptions={resetOptions}
                />
              </EditModeCardSection>
              <EditModeCardSection>
                <TypesEditFields productionTypesField={productionTypesField} type={type} />
                {getProductionTypeValue(values, PERFORMANCE_TYPES_ID.FESTIVAL) && (
                  <PerformanceTypeInfoBlock title={t(`${TP}.FN_PART_OF_FESTIVAL_PRODUCTION`)}>
                    <FestivalsEditFields
                      values={values}
                      festivalsField={festivalsField}
                      festivalsList={festivals?.options}
                      searchFunc={getFestivals}
                      resetOptions={resetOptions}
                      loadingValue={festivals?.fieldName}
                      classes={classes}
                      setFieldValue={setFieldValue}
                      mainFestival={type === EDITING_PRODUCTION_ENTITY_TYPES.FESTIVAL ? company : null}
                    />
                  </PerformanceTypeInfoBlock>
                )}
                {getProductionTypeValue(values, IS_CO_PRODUCED) && (
                  <PerformanceTypeInfoBlock title={t(`${TP}.FN_CO_PRODUCED_PRODUCTION`)}>
                    <CoProducedCompaniesEditFields
                      values={values}
                      coProducedCompaniesField={coProducedCompaniesField}
                      companiesList={companiesList}
                      searchFunc={getCompaniesList}
                      resetOptions={resetOptions}
                      loadingValue={loadingCompanyFieldName}
                      classes={classes}
                      setFieldValue={setFieldValue}
                    />
                  </PerformanceTypeInfoBlock>
                )}
                {getProductionTypeValue(values, PERFORMANCE_TYPES_ID.LIVESTREAM) && (
                  <PerformanceTypeInfoBlock title={t(`${TP}.FN_LIVESTREAM`)}>
                    <DisclaimerBox text={t(`${TP}.FN_LIVESTREAM_DETAIL_INFO`)} className={classes.addFields} />
                  </PerformanceTypeInfoBlock>
                )}
                {getProductionTypeValue(values, PERFORMANCE_TYPES_ID.VIDEO_ON_DEMAND) && (
                  <PerformanceTypeInfoBlock title={t(`${TP}.FN_VIDEO_ON_DEMAND`)}>
                    <DisclaimerBox text={t(`${TP}.FN_VIDEO_ON_DEMAND_DETAIL_INFO`)} className={classes.addFields} />
                  </PerformanceTypeInfoBlock>
                )}
              </EditModeCardSection>
              <EditModeCardSection className={classes.companyCheckboxSection}>
                <MainCompanyFields
                  classes={classes}
                  values={values}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                  companyField={companyField}
                  isCompanyUnknownField={isCompanyUnknownField}
                  companiesList={companiesList}
                  getCompaniesList={getCompaniesList}
                  production={production}
                  resetOptions={resetOptions}
                  loadingCompanyFieldName={loadingCompanyFieldName}
                  isCompanyEdit={type === EDITING_PRODUCTION_ENTITY_TYPES.COMPANY}
                />
              </EditModeCardSection>
              <EditModeCardSection>
                <LangsEditFields
                  values={values}
                  languageField={languageField}
                  subtitlesField={subtitlesField}
                  langs={langs}
                  classNames={classes}
                />
              </EditModeCardSection>
              <EditModeCardSection>
                <BookingInfoEditFields
                  values={values}
                  productionPageField={productionPageField}
                  boxOfficePageField={boxOfficePageField}
                  bookingDateField={bookingDateField}
                  bookingTimeField={bookingTimeField}
                  classes={classes}
                  setFieldValue={setFieldValue}
                />
              </EditModeCardSection>
            </FormikOnError>
          </Form>
        )}
      </Formik>
    </EditModeCard>
  );
};

ProductionUpdateMusicalWorkForm.propTypes = {
  production: PropTypes.object,
  composers: PropTypes.array,
  works: PropTypes.shape({
    options: PropTypes.array,
    loading: PropTypes.bool,
  }),
  festivals: PropTypes.shape({
    options: PropTypes.array,
    fieldName: PropTypes.string,
  }),
  loadingAggValue: PropTypes.string,
  companiesList: PropTypes.array,
  loadingCompanyFieldName: PropTypes.string,
  langs: PropTypes.array,
  onWorkChange: PropTypes.func,
  company: PropTypes.object,
  type: PropTypes.oneOf(Object.values(EDITING_PRODUCTION_ENTITY_TYPES)),
};

ProductionUpdateMusicalWorkForm.defaultProps = {
  onWorkChange: () => {},
};

const mapStateToProps = createStructuredSelector({
  composers: selectComposersOptions(),
  works: selectWorksOptionsData(),
  festivals: selectFestivalsOptions(),
  loadingAggValue: selectAggregationsLoading(),
  loadingCompanyFieldName: selectLoadingFieldName(),
  companiesList: selectCompaniesOptions(),
  langs: selectLanguagesOptions(),
});

export default compose(memo, connect(mapStateToProps))(ProductionUpdateMusicalWorkForm);

const productionUpdateMusicalWorkSchema = t => {
  let allCoProducedValues = [];
  let allLangValues = [];
  let allSurtitleLangValues = [];
  let values = {};
  return object().shape({
    [composerField.name]: object()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [workField.name]: object()
      .test('test', `${TP}.FN_DUPLICATED_VALUE`, function(val) {
        return !this.parent[doubleWorks.name].find(el => el?.work?.value && el.work.value === val?.value);
      })
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [doubleWorks.name]: array()
      .test('get-values', '', function() {
        values = this.parent;
        return true;
      })
      .of(
        object().shape({
          work: object()
            .test('test', `${TP}.FN_DUPLICATED_VALUE`, function(val) {
              const mainWorkId = get(values, 'work_id.value', null);
              const isNotEqualMainWork = mainWorkId && val?.value ? mainWorkId !== val.value : true;
              const noDuplicatesInDoubleWorks =
                values[doubleWorks.name].filter(el => el?.work?.value && el.work.value === val?.value).length < 2;
              return isNotEqualMainWork && noDuplicatesInDoubleWorks;
            })
            .nullable(),
        }),
      ),
    [isCompanyUnknownField.name]: bool(),
    [companyField.name]: object()
      .nullable()
      .when(isCompanyUnknownField.name, (isUnknown, schema) => {
        if (isUnknown) {
          return schema;
        }
        return schema.required(`${TP}.FN_REQUIRED_FIELD`);
      }),
    [coProducedCompaniesField.name]: array()
      .test('all-co-produced', '', function(value) {
        allCoProducedValues = value;
        return true;
      })
      .of(
        object()
          .shape({
            label: string().test('duplicate', t(`${TP}.FN_DUPLICATED_VALUE`), function(value) {
              return (
                allCoProducedValues.filter(
                  el => value && el?.label && el?.label.toLowerCase().trim() === value.toLowerCase().trim(),
                ).length <= 1
              );
            }),
          })
          .nullable(),
      ),
    [productionTypesField.name]: object().shape({
      [PERFORMANCE_TYPES_ID.FESTIVAL]: bool(),
    }),
    [festivalsField.name]: array().when(productionTypesField.name, (types, schema) => {
      if (get(types, PERFORMANCE_TYPES_ID.FESTIVAL)) {
        return schema.of(
          object()
            .required(`${TP}.FN_REQUIRED_FIELD`)
            .nullable(),
        );
      }
      return schema;
    }),
    [languageField.name]: array()
      .test('all-langs', '', function(value) {
        allLangValues = value;
        return true;
      })
      .of(
        object()
          .shape({
            label: string().test('duplicate', t(`${TP}.FN_DUPLICATED_VALUE`), function(value) {
              return (
                !value ||
                allLangValues.filter(el => el?.label && el?.label.toLowerCase().trim() === value.toLowerCase().trim())
                  .length === 1
              );
            }),
          })
          .nullable(),
      ),
    [subtitlesField.name]: array()
      .test('all-langs', '', function(value) {
        allSurtitleLangValues = value;
        return true;
      })
      .of(
        object()
          .shape({
            label: string().test('duplicate', t(`${TP}.FN_DUPLICATED_VALUE`), function(value) {
              return (
                !value ||
                allSurtitleLangValues.filter(
                  el => el?.label && el?.label.toLowerCase().trim() === value.toLowerCase().trim(),
                ).length === 1
              );
            }),
          })
          .nullable(),
      ),
    [productionPageField.name]: string()
      .matches(urlRegex, `${TP}.FN_WEB_LINK_INVALID`)
      .nullable(),
    [boxOfficePageField.name]: string()
      .matches(urlRegex, `${TP}.FN_WEB_LINK_INVALID`)
      .nullable(),
    [bookingTimeField.name]: string()
      .trim()
      .nullable()
      .length(5, `${TP}.FN_INVALID_TIME`),
  });
};
